
import React, { useRef, useEffect } from 'react';
import { Snowflake } from '../types';

const SnowflakeCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const snowflakes = useRef<Snowflake[]>([]);
  const mouse = useRef({ x: -9999, y: -9999 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    const createSnowflakes = () => {
      const count = Math.floor(window.innerWidth / 10);
      snowflakes.current = [];
      const colors = ['#8a2be2', '#00f5ff'];
      for (let i = 0; i < count; i++) {
        snowflakes.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 3 + 1,
          vx: Math.random() * 1 - 0.5,
          vy: Math.random() * 1 + 0.5,
          alpha: Math.random() * 0.5 + 0.2,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }
    };

    createSnowflakes();

    const handleMouseMove = (e: MouseEvent) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };
    window.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      snowflakes.current.forEach((flake) => {
        // Mouse interaction
        const dx = mouse.current.x - flake.x;
        const dy = mouse.current.y - flake.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const forceDirectionX = dx / distance;
        const forceDirectionY = dy / distance;
        const maxDistance = 100;
        const force = (maxDistance - distance) / maxDistance;
        
        if (distance < maxDistance) {
          flake.x -= forceDirectionX * force * 2;
          flake.y -= forceDirectionY * force * 2;
        }

        // Movement
        flake.x += flake.vx;
        flake.y += flake.vy;

        // Boundary check
        if (flake.y > canvas.height + flake.radius) {
          flake.x = Math.random() * canvas.width;
          flake.y = -flake.radius;
        }
        if (flake.x > canvas.width + flake.radius) {
          flake.x = -flake.radius;
        } else if (flake.x < -flake.radius) {
          flake.x = canvas.width + flake.radius;
        }

        // Drawing
        ctx.beginPath();
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2);
        ctx.fillStyle = flake.color;
        ctx.globalAlpha = flake.alpha;
        ctx.shadowBlur = 10;
        ctx.shadowColor = flake.color;
        ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed top-0 left-0 -z-10" />;
};

export default SnowflakeCanvas;
